import {
  FormHelperText,
  Flex,
  Button,
  Box,
  Stack,
  Input,
  FormLabel,
  FormControl,
  Form,
  FormErrorMessage,
  useToast
} from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import { emailRegex } from '../../../../../constants/regexes'
import { useAddPartnerStep } from '../../../../../hooks/homebotNetwork/verificationFlow/useAddPartnerStep'

export const MSG = defineMessages({
  add: {
    id: 'AddPreferredAgentForm.add',
    defaultMessage: 'Yes, add'
  },
  email: {
    id: 'AddPreferredAgentForm.email',
    defaultMessage: 'Email'
  },
  emailInvalid: {
    id: 'AddPreferredAgentForm.emailInvalid',
    defaultMessage: 'Invalid email address'
  },
  emailRequired: {
    id: 'AddPreferredAgentForm.emailRequired',
    defaultMessage: 'Email is required'
  },
  error: {
    id: 'AddPreferredAgentForm.error',
    defaultMessage: 'Failed to add partner'
  },
  firstName: {
    id: 'AddPreferredAgentForm.firstName',
    defaultMessage: 'First name'
  },
  firstNameRequired: {
    id: 'AddPreferredAgentForm.firstNameRequired',
    defaultMessage: 'First name is required'
  },
  lastName: {
    id: 'AddPreferredAgentForm.lastName',
    defaultMessage: 'Last name'
  },
  lastNameRequired: {
    id: 'AddPreferredAgentForm.lastNameRequired',
    defaultMessage: 'Last name is required'
  },
  notAtThisTime: {
    id: 'AddPreferredAgentForm.notAtThisTime',
    defaultMessage: 'Not at this time'
  }
})

/**
 * AddPartnerView Component
 */
const AddPartnerView = () => {
  const { isLoading, onCancel, addPartner } = useAddPartnerStep()
  const toast = useToast()
  const intl = useIntl()

  const handleAddPartner = async data => {
    try {
      await addPartner(data)
    } catch (error) {
      toast({
        description: intl.formatMessage(MSG.error),
        status: 'error'
      })
    }
  }

  return (
    <Box data-qa='add-partner-view' data-testid='add-partner-view'>
      {/* Form is copied from TeamPanel for now */}
      {/* @ts-ignore There is an open DS issue to resolve this */}
      <Form onSubmit={handleAddPartner} noValidate>
        {({ formState: { errors } }) => (
          <Stack gap={4}>
            <Flex direction='column' gap={4}>
              <FormControl isRequired isInvalid={!!errors.firstName}>
                <FormLabel>{intl.formatMessage(MSG.firstName)}</FormLabel>
                <Input
                  name='firstName'
                  placeholder={intl.formatMessage(MSG.firstName)}
                  required
                  tracking={{
                    guid: 'dmRqY3WoqEqFyYSHVg84ZP',
                    ui_context: 'verification_flow_add_partner_view',
                    descriptor: MSG.firstName.defaultMessage
                  }}
                  rules={{ required: `${intl.formatMessage(MSG.firstNameRequired)}` }}
                />
                {errors.firstName ? (
                  <>
                    <FormErrorMessage>{errors.firstName.message?.toString()}</FormErrorMessage>
                  </>
                ) : (
                  <>
                    <FormHelperText>{'\u200B'}</FormHelperText>
                  </>
                )}
              </FormControl>
              <FormControl isRequired isInvalid={!!errors.lastName}>
                <FormLabel>{intl.formatMessage(MSG.lastName)}</FormLabel>
                <Input
                  name='lastName'
                  placeholder={intl.formatMessage(MSG.lastName)}
                  required
                  tracking={{
                    guid: '4kz6vbdgZgsYg3BUhWwacE',
                    ui_context: 'verification_flow_add_partner_view',
                    descriptor: MSG.lastName.defaultMessage
                  }}
                  rules={{ required: `${intl.formatMessage(MSG.lastNameRequired)}` }}
                />
                {errors.lastName ? (
                  <>
                    <FormErrorMessage>{errors.lastName.message?.toString()}</FormErrorMessage>
                  </>
                ) : (
                  <>
                    <FormHelperText>{'\u200B'}</FormHelperText>
                  </>
                )}
              </FormControl>
              <FormControl isRequired isInvalid={!!errors.email}>
                <FormLabel>{intl.formatMessage(MSG.email)}</FormLabel>
                <Input
                  name='email'
                  type='email'
                  placeholder={intl.formatMessage(MSG.email)}
                  required
                  tracking={{
                    guid: 'tB1ameFz76rcMmywPXJLiS',
                    ui_context: 'verification_flow_add_partner_view',
                    descriptor: MSG.email.defaultMessage
                  }}
                  rules={{
                    // TODO: Share these w/ tune value form translations
                    required: intl.formatMessage(MSG.emailRequired),
                    pattern: {
                      value: emailRegex,
                      message: intl.formatMessage(MSG.emailInvalid)
                    }
                  }}
                />
                {errors.email ? (
                  <>
                    <FormErrorMessage>{errors.email.message?.toString()}</FormErrorMessage>
                  </>
                ) : (
                  <>
                    <FormHelperText>{'\u200B'}</FormHelperText>
                  </>
                )}
              </FormControl>
            </Flex>

            <Stack direction='row' gap={2} justifyContent='flex-end'>
              <Button
                variant='solid'
                colorScheme='secondary'
                isDisabled={isLoading}
                onClick={onCancel}
                tracking={{
                  guid: 'omoQkyaEqhKrX6Gwx6b7yL',
                  ui_context: 'verification_flow_add_partner_view',
                  descriptor: MSG.notAtThisTime.defaultMessage
                }}
              >
                {intl.formatMessage(MSG.notAtThisTime)}
              </Button>
              <Button
                mr={3}
                colorScheme='primary'
                type='submit'
                isLoading={isLoading}
                tracking={{
                  guid: '5UEdaEeK4tsp2twTigB4Zu',
                  ui_context: 'verification_flow_add_partner_view',
                  descriptor: MSG.add.defaultMessage
                }}
              >
                {intl.formatMessage(MSG.add)}
              </Button>
            </Stack>
          </Stack>
        )}
      </Form>
    </Box>
  )
}

export default AddPartnerView
